import { ref } from 'vue'
import { Buffer } from 'buffer'
import { Keypair, VersionedTransaction } from '@solana/web3.js'
import bs58 from 'bs58'
import { CHAIN_RPCS } from '@/utils/consts'
import { useLocalKeys } from './useLocalKeys'
import { useWeb3 } from './useWeb3'
import { use1InchStore } from '@/stores/oneInchStore'
import { useJupStore } from '@/stores/jupiterStore'
import type { SwapResponse } from '@/services/oneinch-v2-service2/types'
import type { JupOrderResponse } from '@/services/jupiter-service/types'

export interface EvmSwapParams {
  chain: string
  tokenFrom: string
  tokenTo: string
  amount: bigint
  slippage: number
}

export interface SolSwapParams {
  inputMint: string
  outputMint: string
  amount: number | bigint
}

export interface EvmSwapResult {
  hash: string
  receipt: unknown
  swap: SwapResponse
}

export interface SolSwapResult {
  signature: string
  result: unknown
  order: JupOrderResponse
}

export const useDexTrade = () => {
  const localKeys = useLocalKeys()
  const oneInchStore = use1InchStore()
  const jupStore = useJupStore()

  const loading = ref(false)
  const error = ref<string | null>(null)

  const evmPrivateKey = () => localKeys.get('oneinch-key-evm')
  const oneinchApiKey = () => localKeys.get('oneinch-api')
  const jupPrivateKey = () => localKeys.get('jup-xprv')
  const jupApiKey = () => localKeys.get('jup-api')

  const swapEvm = async (params: EvmSwapParams): Promise<EvmSwapResult> => {
    const privateKey = evmPrivateKey()
    const apiKey = oneinchApiKey()
    if (!privateKey) {
      throw new Error('EVM private key not found in localStorage (key: oneinch-key-evm)')
    }
    if (!apiKey) {
      throw new Error('1inch API key not found in localStorage (key: oneinch-api)')
    }

    oneInchStore.apiKey = apiKey

    const { initWeb3, getPublicKey, swap } = useWeb3()
    const rpcs = CHAIN_RPCS[params.chain]
    if (!rpcs?.length) {
      throw new Error(`Unsupported chain: ${params.chain}`)
    }
    initWeb3(rpcs)

    const publicKey = getPublicKey(privateKey)

    loading.value = true
    error.value = null
    try {
      const res = await swap(
        params.chain,
        params.tokenFrom,
        params.tokenTo,
        params.amount,
        params.slippage,
        publicKey,
        privateKey,
      )
      if (!res) {
        throw new Error('Swap returned empty result')
      }
      const hash =
        (res.result as unknown as Record<string, string>)?.transactionHash ??
        (res.result as unknown as Record<string, string>)?.blockHash ??
        ''
      return { hash, receipt: res.result, swap: res.swap }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      error.value = msg
      throw e
    } finally {
      loading.value = false
    }
  }

  const buyEvm = async (
    chain: string,
    paymentToken: string,
    tokenAddress: string,
    amount: bigint,
    slippage: number,
  ) => swapEvm({ chain, tokenFrom: paymentToken, tokenTo: tokenAddress, amount, slippage })

  const sellEvm = async (
    chain: string,
    tokenAddress: string,
    paymentToken: string,
    amount: bigint,
    slippage: number,
  ) => swapEvm({ chain, tokenFrom: tokenAddress, tokenTo: paymentToken, amount, slippage })

  const swapSol = async (params: SolSwapParams): Promise<SolSwapResult> => {
    const privateKey = jupPrivateKey()
    const apiKey = jupApiKey()
    if (!privateKey) {
      throw new Error('Jupiter private key not found in localStorage (key: jup-xprv)')
    }
    if (!apiKey) {
      throw new Error('Jupiter API key not found in localStorage (key: jup-api)')
    }

    jupStore.apiKey = apiKey

    const wallet = Keypair.fromSecretKey(bs58.decode(privateKey))
    const taker = wallet.publicKey.toBase58()

    loading.value = true
    error.value = null
    try {
      const orderData = await jupStore.getOrder(
        params.inputMint,
        params.outputMint,
        params.amount,
        taker,
      )
      if (!orderData || 'error' in orderData) {
        throw new Error(
          (orderData as unknown as Record<string, string>)?.error ?? 'Jupiter order creation failed',
        )
      }

      const jupOrder = orderData as JupOrderResponse
      const transaction = VersionedTransaction.deserialize(
        Buffer.from(jupOrder.transaction, 'base64'),
      )
      transaction.sign([wallet])
      const signedTransaction = Buffer.from(transaction.serialize()).toString('base64')

      const result = await jupStore.execute(signedTransaction, jupOrder.requestId)
      return {
        signature:
          (result as unknown as Record<string, string>)?.signature ??
          (result as unknown as Record<string, string>)?.txid ??
          '',
        result,
        order: jupOrder,
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      error.value = msg
      throw e
    } finally {
      loading.value = false
    }
  }

  const buySol = async (
    paymentMint: string,
    tokenMint: string,
    amount: number | bigint,
  ) => swapSol({ inputMint: paymentMint, outputMint: tokenMint, amount })

  const sellSol = async (
    tokenMint: string,
    paymentMint: string,
    amount: number | bigint,
  ) => swapSol({ inputMint: tokenMint, outputMint: paymentMint, amount })

  const getEvmBalance = async (chain: string, token: string) => {
    const privateKey = evmPrivateKey()
    if (!privateKey) {
      throw new Error('EVM private key not found in localStorage (key: oneinch-key-evm)')
    }
    const { initWeb3, getPublicKey, getTokenBalance } = useWeb3()
    const rpcs = CHAIN_RPCS[chain]
    if (!rpcs?.length) {
      throw new Error(`Unsupported chain: ${chain}`)
    }
    initWeb3(rpcs)
    const publicKey = getPublicKey(privateKey)
    return await getTokenBalance(token, publicKey)
  }

  const getSolBalance = async (address?: string) => {
    const apiKey = jupApiKey()
    const privateKey = jupPrivateKey()
    if (!apiKey) {
      throw new Error('Jupiter API key not found in localStorage (key: jup-api)')
    }

    jupStore.apiKey = apiKey
    let targetAddress = address
    if (!targetAddress) {
      if (!privateKey) {
        throw new Error('Jupiter private key not found in localStorage (key: jup-xprv)')
      }
      const wallet = Keypair.fromSecretKey(bs58.decode(privateKey))
      targetAddress = wallet.publicKey.toBase58()
    }
    return await jupStore.getBalances(targetAddress)
  }

  return {
    loading,
    error,
    swapEvm,
    buyEvm,
    sellEvm,
    swapSol,
    buySol,
    sellSol,
    getEvmBalance,
    getSolBalance,
  }
}
